import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaBed, FaBath, FaVectorSquare, FaPhoneAlt, FaEye, FaCommentDots, FaHeart, FaRegHeart, FaMapMarkerAlt, FaCamera } from "react-icons/fa";
import { setModelShow } from "../../../redux/athentication/Athentication";
import { toggleShortlistProperty, isPropertyShortlisted } from "../../../utils/shortlistUtils";

const LatestPropertyCard = ({ property, onActionClick }) => {
  const dispatch = useDispatch();
  const { isAuth } = useSelector((store) => store.Athentication);
  const [favorite, setFavorite] = useState(false);

  useEffect(() => {
    const syncFav = () => {
      if (property) {
        setFavorite(isPropertyShortlisted(property));
      }
    };
    syncFav();
    window.addEventListener("mgh_shortlist_updated", syncFav);
    return () => window.removeEventListener("mgh_shortlist_updated", syncFav);
  }, [property]);

  if (!property) return null;

  // Image source detection with fallbacks
  const getImageSrc = () => {
    if (property.DeskImg && Array.isArray(property.DeskImg) && property.DeskImg.length > 0 && property.DeskImg[0]) {
      return property.DeskImg[0];
    }
    if (property.photos && Array.isArray(property.photos) && property.photos.length > 0 && property.photos[0]) {
      return property.photos[0];
    }
    if (property.images && Array.isArray(property.images) && property.images.length > 0 && property.images[0]) {
      return property.images[0];
    }
    if (property.mobileimage && Array.isArray(property.mobileimage) && property.mobileimage.length > 0 && property.mobileimage[0]) {
      return property.mobileimage[0];
    }
    if (property.thumbnail) {
      return property.thumbnail;
    }
    return "/assets/images/noimage1.png";
  };

  // Badge 1 (Transaction: Buy, Lease, Rent, etc.)
  const getTransactionBadge = () => {
    const cat = property.category || "";
    const lower = cat.toLowerCase();
    if (lower === "sell" || lower === "sale") return "Buy";
    if (lower === "rent") return "Rent";
    if (lower === "lease") return "Lease";
    if (lower === "pg") return "PG";
    return cat || "Buy";
  };

  // Badge 2 (Property type)
  const getTypeBadge = () => {
    return property.subCategory || property.superCategory || "Flat/Apartment";
  };

  // Location string
  const getLocationString = () => {
    const locality = property.locality;
    const city = property.city || property.state || "Hyderabad";
    if (locality && city) {
      return `${locality}, ${city}`;
    }
    return locality || city || property.subCategory || "Hyderabad";
  };

  // Photo Count
  const getPhotoCount = () => {
    const count = (property.DeskImg && property.DeskImg.length) ||
      (property.photos && property.photos.length) ||
      (property.images && property.images.length) || 1;
    return `${count} Photos`;
  };

  // Price formatting
  const getFormattedPrice = () => {
    const raw = property.visiblePrice || (property.expectedPrice ? `${property.expectedPrice}` : "");
    if (!raw) return "Price on Request";
    const str = String(raw).trim();
    if (str.startsWith("₹") || str.toLowerCase().startsWith("rs")) {
      return str;
    }
    return `₹ ${str}`;
  };

  // Description snippet
  const getDescription = () => {
    if (property.unicProperty && property.unicProperty.trim().length > 0) {
      return property.unicProperty;
    }
    const bhk = property.noofBedrooms ? `${property.noofBedrooms} BHK ` : "";
    const subCat = property.subCategory || "Flat/apartment";
    const cat = property.category === "Sell" ? "Sell" : (property.category || "Sell");
    const apt = property.apartment || property.apartmentName || "prime project";
    const loc = property.locality ? `${property.locality}, ` : "";
    const city = property.city || "Hyderabad";
    const area = property.carpetArea
      ? `Constructed on a Carpet Area of ${property.carpetArea} ${property.carpetAreaMesurmentType || "sq.ft."}, the...`
      : "";
    return `This ${bhk}${subCat} is available for ${cat} in ${apt} ${loc}${city}. ${area}`;
  };

  // Specs details
  const getBhkText = () => {
    if (property.noofBedrooms !== undefined && property.noofBedrooms !== null && property.noofBedrooms !== "") {
      return `${property.noofBedrooms} BHK`;
    }
    return "-";
  };

  const getBathText = () => {
    if (property.noofBathrooms !== undefined && property.noofBathrooms !== null && property.noofBathrooms !== "") {
      return `${property.noofBathrooms} Bath`;
    }
    return "-";
  };

  const getAreaText = () => {
    if (property.carpetArea) {
      return `${property.carpetArea} ${property.carpetAreaMesurmentType || "sq.ft."}`;
    }
    if (property.superbuildupArea) {
      return `${property.superbuildupArea} ${property.superbuildupAreaMesurmentType || "sq.ft."}`;
    }
    return "Area on req.";
  };

  const propertyDetailUrl = `/property-info/${property.apartment || property.propertyID || "property"}/${property.propertyID}`;

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const isAdded = toggleShortlistProperty(property);
    setFavorite(isAdded);
    if (isAdded) {
      toast.success("Property saved to shortlist!", {
        position: "top-center",
        autoClose: 1500,
      });
    } else {
      toast.info("Removed from shortlist", {
        position: "top-center",
        autoClose: 1500,
      });
    }
  };

  return (
    <article className="lp-card">
      {/* Top Image Box */}
      <div className="lp-card-img-wrap">
        <Link to={propertyDetailUrl} className="lp-card-img-link">
          <img
            src={getImageSrc()}
            alt={property.apartment || "Property"}
            className="lp-card-img"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/assets/images/noimage1.png";
            }}
          />
        </Link>

        {/* Top-Left Category & Type Badges */}
        <div className="lp-img-top-left-badges">
          <span className="lp-badge-buy-gold">{getTransactionBadge()}</span>
          <span className="lp-badge-type-white">{getTypeBadge()}</span>
        </div>

        {/* Top-Right Favorite Button */}
        <button
          type="button"
          className={`lp-card-fav-btn ${favorite ? "active" : ""}`}
          onClick={handleFavoriteClick}
          title={favorite ? "Remove from wishlist" : "Add to wishlist"}
          aria-label="Wishlist"
        >
          {favorite ? <FaHeart className="lp-fav-icon-active" /> : <FaRegHeart />}
        </button>

        {/* Bottom-Left Location Overlay */}
        <div className="lp-img-location-badge" title={getLocationString()}>
          <FaMapMarkerAlt className="lp-img-badge-icon" />
          <span>{getLocationString()}</span>
        </div>

        {/* Bottom-Right Photo Count Overlay */}
        <div className="lp-img-photos-badge">
          <FaCamera className="lp-img-badge-icon" />
          <span>{getPhotoCount()}</span>
        </div>
      </div>

      {/* Card Body */}
      <div className="lp-card-body">
        {/* Title */}
        <Link to={propertyDetailUrl} className="lp-card-title-link">
          <h3 className="lp-card-title" title={property.apartment || property.apartmentName || "Featured Property"}>
            {property.apartment || property.apartmentName || "Featured Property"}
          </h3>
        </Link>

        {/* Price */}
        <div className="lp-card-price">
          {getFormattedPrice()}
        </div>

        {/* Description */}
        <p className="lp-card-desc" title={getDescription()}>
          {getDescription()}
        </p>

        {/* Specifications Row */}
        <div className="lp-card-specs">
          <div className="lp-spec-item" title="Bedrooms">
            <span className="lp-spec-icon-wrap"><FaBed /></span>
            <span className="lp-spec-text">{getBhkText()}</span>
          </div>

          <div className="lp-spec-item" title="Bathrooms">
            <span className="lp-spec-icon-wrap"><FaBath /></span>
            <span className="lp-spec-text">{getBathText()}</span>
          </div>

          <div className="lp-spec-item" title="Carpet Area">
            <span className="lp-spec-icon-wrap"><FaVectorSquare /></span>
            <span className="lp-spec-text">{getAreaText()}</span>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="lp-card-actions">
          <Link
            to={propertyDetailUrl}
            className="lp-btn-details"
            title="View Property Details"
          >
            <FaEye className="lp-btn-icon" />
            <span>View Details</span>
          </Link>

          <button
            type="button"
            className="lp-btn-contact"
            onClick={(e) => onActionClick(e, property, "contact")}
            title={`Contact ${property.postUserPosition || "Owner"}`}
          >
            <FaPhoneAlt className="lp-btn-icon" />
            <span>{property.postUserPosition ? `Contact ${property.postUserPosition}` : "Contact Owner"}</span>
          </button>
        </div>
      </div>
    </article>
  );
};

export default LatestPropertyCard;
